import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import Logo from "../components/Logo";
import {
  Lock,
  ShoppingCart,
  TrendingDown,
  Package,
  DollarSign,
  Users,
  BarChart3,
  AlertCircle
} from "lucide-react";

// ============ ADMIN ACCESS CONTROL ============
const DEMO_ADMIN_EMAILS = [
  "reynir@replydriven.com",
  "reyniroskarsson2310@gmail.com"
];
const DEMO_SECRET_KEY = "rd_demo_2026";

// ============ INSIGHT CATEGORIES ============
const INSIGHT_CATEGORIES = [
  {
    id: "conversion",
    title: "Conversion Issues",
    description: "Identify friction points in your checkout and purchase flow",
    icon: ShoppingCart,
    iconBg: "bg-red-500/10",
    iconColor: "text-red-400",
    borderColor: "border-red-500/20"
  },
  {
    id: "product",
    title: "Product Performance",
    description: "Spot underperforming products and inventory risks",
    icon: Package,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
    borderColor: "border-amber-500/20"
  },
  {
    id: "pricing",
    title: "Pricing Opportunities",
    description: "Find AOV gaps and pricing inconsistencies",
    icon: DollarSign,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
    borderColor: "border-emerald-500/20"
  },
  {
    id: "retention",
    title: "Customer Retention",
    description: "Understand repeat purchase behavior and loyalty gaps",
    icon: Users,
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-400",
    borderColor: "border-blue-500/20"
  }
];

const DemoInsightCategories = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const secretKey = searchParams.get("key");
    if (
      (user?.email && DEMO_ADMIN_EMAILS.includes(user.email.toLowerCase())) ||
      secretKey === DEMO_SECRET_KEY
    ) {
      setAuthorized(true);
    } else if (user !== undefined) {
      const timer = setTimeout(() => {
        if (!authorized) navigate("/");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, searchParams, navigate, authorized]);

  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-[#262626] bg-[#050505]">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between">
          <Logo showText />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-8 py-16">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#D4F34A]/10 border border-[#D4F34A]/20 rounded-full px-4 py-2 mb-6">
            <BarChart3 className="w-4 h-4 text-[#D4F34A]" />
            <span className="text-sm text-[#D4F34A] font-medium">Weekly AI Analysis</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Insights tailored to your store
          </h1>
          <p className="text-text-muted text-lg max-w-2xl mx-auto">
            Every Monday, we analyze your Shopify data and surface one clear, actionable insight.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {INSIGHT_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <Card 
                key={category.id} 
                className={`bg-surface border-[#262626] hover:${category.borderColor} transition-colors`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${category.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${category.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-white mb-2">{category.title}</h3>
                      <p className="text-text-muted">{category.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-text-muted text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>One focused insight per week. No dashboard overwhelm.</span>
          </div>
        </div>
      </main>

      {/* Demo Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#050505] border-t border-[#262626] py-2 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-xs text-text-muted">
          <span>Internal Demo • Not for public distribution</span>
          <span>Insight Categories Grid</span>
        </div>
      </footer>
    </div>
  );
};

export default DemoInsightCategories;
